import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PermProvider } from "@/context/PermContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Patients from "@/pages/Patients";
import PatientProfile from "@/pages/PatientProfile";
import Doctors from "@/pages/Doctors";
import OPD from "@/pages/OPD";
import OPDConsultation from "@/pages/OPDConsultation";
import IPD from "@/pages/IPD";
import IPDDashboard from "@/pages/IPDDashboard";
import IPDStickerPrint from "@/pages/IPDStickerPrint";
import Emergency from "@/pages/Emergency";
import Appointments from "@/pages/Appointments";
import Investigations from "@/pages/Investigations";
import DoctorInvestigationReport from "@/pages/DoctorInvestigationReport";
import WalkinInvestigation from "@/pages/WalkinInvestigation";
import Training from "@/pages/Training";
import PatientLink from "@/pages/PatientLink";
import Pharmacy from "@/pages/Pharmacy";
import Billing from "@/pages/Billing";
import OT from "@/pages/OT";
import BedManagement from "@/pages/BedManagement";
import PrescriptionPrint from "@/pages/PrescriptionPrint";
import DischargePrint from "@/pages/DischargePrint";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import PMJAY from "@/pages/PMJAY";
import ABDM from "@/pages/ABDM";
import Commission from "@/pages/Commission";
import AuditLogs from "@/pages/AuditLogs";
import HRMS from "@/pages/HRMS";
import PrintCenter from "@/pages/PrintCenter";
import WhatsApp from "@/pages/WhatsApp";
import PayslipPrint from "@/pages/PayslipPrint";
import DoctorProfile from "@/pages/DoctorProfile";
import DoctorDashboard from "@/pages/DoctorDashboard";
import ReportPrint from "@/pages/ReportPrint";
import UserManagement from "@/pages/UserManagement";
import DiscountApprovals from "@/pages/DiscountApprovals";
import BillPrint from "@/pages/BillPrint";
import Kiosk from "@/pages/Kiosk";
import KioskSettings from "@/pages/KioskSettings";
import InvestigationMaster from "@/pages/InvestigationMaster";
import LabMaster from "@/pages/LabMaster";
import Subscription from "@/pages/Subscription";
import PublicLabReport from "@/pages/PublicLabReport";
import DebitTxns from "@/pages/DebitTxns";
import Credits from "@/pages/Credits";
import ShortcutsProvider from "@/context/ShortcutsProvider";
import { BrandingProvider } from "@/context/BrandingContext";
import BrandingSettings from "@/pages/BrandingSettings";
import SuperAdmin from "@/pages/SuperAdmin";

function Protected({ children }) {
  const { user, checking } = useAuth();
  if (checking) return <div className="p-8 text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Platform admins live in the super-admin console only — bounce them.
  if (user.role === "platform_admin") return <Navigate to="/super-admin" replace />;
  return <Layout>{children}</Layout>;
}

function PlatformOnly({ children }) {
  const { user, checking } = useAuth();
  if (checking) return <div className="p-8 text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/" replace />;
  return children;
}

function App() {
  useEffect(() => {
    const TITLE = "The Vyomra HOS | Divine Multispeciality Hospital";
    document.title = TITLE;
    const t = setInterval(() => {
      if (document.title !== TITLE) {
        document.title = TITLE;
      }
    }, 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <BrandingProvider>
        <AuthProvider>
          <PermProvider>
          <ShortcutsProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/super-admin" element={<PlatformOnly><SuperAdmin /></PlatformOnly>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/patients" element={<Protected><Patients /></Protected>} />
            <Route path="/patients/:id" element={<Protected><PatientProfile /></Protected>} />
            <Route path="/doctors" element={<Protected><Doctors /></Protected>} />
            <Route path="/doctors/:id" element={<Protected><DoctorProfile /></Protected>} />
            <Route path="/doctor-dashboard" element={<Protected><DoctorDashboard /></Protected>} />
            <Route path="/opd" element={<Protected><OPD /></Protected>} />
            <Route path="/opd/:id" element={<Protected><OPDConsultation /></Protected>} />
            <Route path="/ipd" element={<Protected><IPD /></Protected>} />
            <Route path="/ipd/:id" element={<Protected><IPDDashboard /></Protected>} />
            <Route path="/ipd/:id/stickers" element={<IPDStickerPrint />} />
            <Route path="/emergency" element={<Protected><Emergency /></Protected>} />
            <Route path="/appointments" element={<Protected><Appointments /></Protected>} />
            <Route path="/investigations" element={<Protected><Investigations /></Protected>} />
            <Route path="/reports/investigations/by-doctor" element={<Protected><DoctorInvestigationReport /></Protected>} />
            <Route path="/walkin-investigations" element={<Protected><WalkinInvestigation /></Protected>} />
            <Route path="/training" element={<Protected><Training /></Protected>} />
            <Route path="/patient-link" element={<Protected><PatientLink /></Protected>} />
            <Route path="/pharmacy" element={<Protected><Pharmacy /></Protected>} />
            <Route path="/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/ot" element={<Protected><OT /></Protected>} />
            <Route path="/beds" element={<Protected><BedManagement /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/pmjay" element={<Protected><PMJAY /></Protected>} />
            <Route path="/abdm" element={<Protected><ABDM /></Protected>} />
            <Route path="/commission" element={<Protected><Commission /></Protected>} />
            <Route path="/hrms" element={<Protected><HRMS /></Protected>} />
            <Route path="/print-center" element={<Protected><PrintCenter /></Protected>} />
            <Route path="/audit" element={<Protected><AuditLogs /></Protected>} />
            <Route path="/whatsapp" element={<Protected><WhatsApp /></Protected>} />
            <Route path="/prescription/:opdId/print" element={<PrescriptionPrint />} />
            <Route path="/discharge/:ipdId/print" element={<DischargePrint />} />
            <Route path="/payslip/:id/print" element={<PayslipPrint />} />
            <Route path="/reports/print" element={<ReportPrint />} />
            <Route path="/users" element={<Protected><UserManagement /></Protected>} />
            <Route path="/approvals" element={<Protected><DiscountApprovals /></Protected>} />
            <Route path="/investigations-master" element={<Protected><InvestigationMaster /></Protected>} />
            <Route path="/lab-master" element={<Protected><LabMaster /></Protected>} />
            <Route path="/subscription" element={<Protected><Subscription /></Protected>} />
            {/* Public QR-shareable lab report — unauthenticated, token-gated */}
            <Route path="/lab/report/:token" element={<PublicLabReport />} />
            <Route path="/debit" element={<Protected><DebitTxns /></Protected>} />
            <Route path="/credits" element={<Protected><Credits /></Protected>} />
            <Route path="/bill/opd/:opdId/print" element={<BillPrint />} />
            <Route path="/bill/ipd/:ipdId/print" element={<BillPrint />} />
            {/* Kiosk · public, no auth wrapper (physically deployed device) */}
            <Route path="/kiosk" element={<Kiosk />} />
            <Route path="/kiosk-settings" element={<Protected><KioskSettings /></Protected>} />
            <Route path="/branding-settings" element={<Protected><BrandingSettings /></Protected>} />
          </Routes>
          </ShortcutsProvider>
          </PermProvider>
        </AuthProvider>
        </BrandingProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
